/**
 * Replay API - Type-safe replay endpoints
 * Matches backend: app/api/v1/routes/replays.py
 */

import { apiClient } from './apiClient';
import type { ReplayQueryParams, ReplayHistoryResponse } from '../../types/rest';
import type { FogView, TruthView } from '../../types/game';

/**
 * Get replay by match ID with optional turn and view
 * GET /api/v1/replays/{match_id}
 * Query: turn (default 0), view (white|black|truth, default white)
 * 200 -> FogView (white/black) or TruthView (truth, only for finished matches)
 * 400 -> invalid turn/view
 * 404 -> not found
 * 403 -> truth view not allowed for in-progress match
 * 503 -> replays disabled / L2 down
 */
export async function getReplay(
  matchId: string,
  params?: ReplayQueryParams
): Promise<FogView | TruthView> {
  const response = await apiClient.get<FogView | TruthView>(`/replays/${matchId}`, {
    params,
  });
  
  // Transform response to internal types
  return transformReplayResponse(response.data);
}

/**
 * Get replay history for authenticated user
 * GET /api/v1/replays/history
 * Query: limit (1-200, default 50), offset (default 0)
 * 200 -> ReplayHistoryResponse
 * 401 -> unauthorized
 * 503 -> replays disabled / L2 down
 */
export async function getReplayHistory(
  params?: { limit?: number; offset?: number }
): Promise<ReplayHistoryResponse> {
  const response = await apiClient.get<ReplayHistoryResponse>('/replays/history', { params });
  return response.data;
}

interface FogViewResponse {
  own_pieces: { square: string; type: string }[];
  opponent_squares: { square: string; hidden: true }[];
  graveyard: { square: string; type: string; color: 'white' | 'black'; captured_by_color: 'white' | 'black' }[];
  turn: 'white' | 'black';
  in_check_for_viewer: boolean;
}

interface TruthViewResponse {
  white_pieces: { square: string; type: string }[];
  black_pieces: { square: string; type: string }[];
  graveyard: { square: string; type: string; color: 'white' | 'black'; captured_by_color: 'white' | 'black' }[];
  turn: 'white' | 'black';
  in_check_for_white: boolean;
  in_check_for_black: boolean;
}

/**
 * Transform backend replay response to internal types
 */
function transformReplayResponse(
  data: FogViewResponse | TruthViewResponse
): FogView | TruthView {
  if ('white_pieces' in data) {
    // TruthView
    return {
      white_pieces: data.white_pieces.map(p => ({ square: p.square, type: p.type as any })),
      black_pieces: data.black_pieces.map(p => ({ square: p.square, type: p.type as any })),
      graveyard: data.graveyard.map(g => ({
        square: g.square,
        type: g.type as any,
        color: g.color,
        captured_by_color: g.captured_by_color,
      })),
      turn: data.turn,
      in_check_for_white: data.in_check_for_white,
      in_check_for_black: data.in_check_for_black,
    } as TruthView;
  } else {
    // FogView
    return {
      own_pieces: data.own_pieces.map(p => ({ square: p.square, type: p.type as any })),
      opponent_squares: data.opponent_squares.map(s => ({ square: s.square, hidden: true })),
      graveyard: data.graveyard.map(g => ({
        square: g.square,
        type: g.type as any,
        color: g.color,
        captured_by_color: g.captured_by_color,
      })),
      turn: data.turn,
      in_check_for_viewer: data.in_check_for_viewer,
    } as FogView;
  }
}

interface FogViewResponse {
  own_pieces: { square: string; type: string }[];
  opponent_squares: { square: string; hidden: true }[];
  graveyard: { square: string; type: string; color: 'white' | 'black'; captured_by_color: 'white' | 'black' }[];
  turn: 'white' | 'black';
  in_check_for_viewer: boolean;
}

interface TruthViewResponse {
  white_pieces: { square: string; type: string }[];
  black_pieces: { square: string; type: string }[];
  graveyard: { square: string; type: string; color: 'white' | 'black'; captured_by_color: 'white' | 'black' }[];
  turn: 'white' | 'black';
  in_check_for_white: boolean;
  in_check_for_black: boolean;
}

/**
 * Get match PGN
 * GET /api/v1/replays/{match_id}/pgn
 * 200 -> { pgn: string }
 * 404 -> not found
 */
export async function getMatchPgn(matchId: string): Promise<{ pgn: string }> {
  const response = await apiClient.get<{ pgn: string }>(`/replays/${matchId}/pgn`);
  return response.data;
}

/**
 * Get match FEN at specific turn
 * GET /api/v1/replays/{match_id}/fen
 * Query: turn (default current)
 * 200 -> { fen: string }
 * 400 -> invalid turn
 * 404 -> not found
 */
export async function getMatchFen(
  matchId: string,
  turn?: number
): Promise<{ fen: string }> {
  const response = await apiClient.get<{ fen: string }>(`/replays/${matchId}/fen`, {
    params: { turn },
  });
  return response.data;
}

/**
 * Replay error codes
 */
export const REPLAY_ERROR_CODES = {
  INVALID_TURN: 'invalid_turn',
  INVALID_VIEW: 'invalid_view',
  NOT_FOUND: 'not_found',
  TRUTH_VIEW_DENIED: 'truth_view_denied',
  FEATURE_DISABLED: 'feature_disabled',
  L2_UNAVAILABLE: 'l2_unavailable',
} as const;

export type ReplayErrorCode = typeof REPLAY_ERROR_CODES[keyof typeof REPLAY_ERROR_CODES];

/**
 * Check if error is specific replay error code
 */
export function isReplayError(error: unknown, code: ReplayErrorCode): boolean {
  if (!error || typeof error !== 'object') return false;
  const apiError = error as { errorCode?: string; status?: number };
  return apiError.errorCode === code;
}

/**
 * Check if error is truth view denied (403)
 */
export function isTruthViewDenied(error: unknown): boolean {
  return isReplayError(error, REPLAY_ERROR_CODES.TRUTH_VIEW_DENIED);
}

/**
 * Check if error is replay not found (404)
 */
export function isReplayNotFound(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const apiError = error as { status?: number; errorCode?: string };
  return apiError.status === 404 || apiError.errorCode === REPLAY_ERROR_CODES.NOT_FOUND;
}